import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box, Checkbox, Typography, CircularProgress, ListItemText, MenuItem, FormControl, Select, Chip } from "@mui/material";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { RootState, useAppDispatch } from "../../store";
import { Agent, EHR, SuperOrganization } from "../../types";
import { updateAgentThunk } from "../../slices/AgentSlice";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { InputStyles, menuPropsStyles } from "../../styles/GeneralStyles";
import { Colors } from "../../Colors";
import FunctionalModulesLayout from "./FunctionalModulesLayout";
import { PrimaryButton } from "../../subcomponents/CustomButton";
import { api } from "../../utils/utils";
import { setSuperOrganization } from "../../slices/AuthSlice";

export function PlanOfCare() {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const agents = useSelector((state: RootState) => state.agents.agents);
  const agent = agents && agents.length > 0 ? agents[0] : null;
  const organization = useSelector((state: RootState) => state.auth.organization);
  const superOrganization = useSelector((state: RootState) => state.auth.superOrganization);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [defaultGapBetweenAppointments, setDefaultGapBetweenAppointments] = useState<number>(0);
  const [autoExtendToWeeks, setAutoExtendToWeeks] = useState<boolean>(false);
  const [numberOfWeeksToAutoExtend, setNumberOfWeeksToAutoExtend] = useState<number>(0);
  const [autoExtendToAuth, setAutoExtendToAuth] = useState<boolean>(false);
  const [autoExtendToPoc, setAutoExtendToPoc] = useState<boolean>(false);
  const [autoTruncateAppointments, setAutoTruncateAppointments] = useState<boolean>(false);

  const [addToWaitlistForUnscheduledVisitsOnPocCreated, setAddToWaitlistForUnscheduledVisitsOnPocCreated] = useState<boolean>(false);

  // Automated POC booking link sending state
  const [automatedPocEnabled, setAutomatedPocEnabled] = useState<boolean>(false);
  const [pocSendDayOfWeek, setPocSendDayOfWeek] = useState<number>(0); // 0 = Sunday
  const [pocSendTime, setPocSendTime] = useState<DateTime | null>(DateTime.now().set({ hour: 9, minute: 0, second: 0 }));
  const [pocAutoSendNextWeek, setPocAutoSendNextWeek] = useState<boolean>(false);

  const daysOfWeek = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ];

  useEffect(() => {
    if (superOrganization) {
      setDefaultGapBetweenAppointments(superOrganization.defaultGapBetweenAppointments || 0);
      setAutoExtendToWeeks(superOrganization.autoExtendToWeeks || false);
      setNumberOfWeeksToAutoExtend(superOrganization.numberOfWeeksToAutoExtend || 0);
      setAutoExtendToAuth(superOrganization.autoExtendToAuth || false);
      setAutoExtendToPoc(superOrganization.autoExtendToPoc || false);
      setAutoTruncateAppointments(superOrganization.autoTruncateAppointments || false);
    }
  }, [superOrganization]);

  useEffect(() => {
    if (agent) {
      setAutomatedPocEnabled(agent.automatedPocEnabled || false);
      setPocSendDayOfWeek(agent.pocSendDayOfWeek ?? 0);
      if (agent.pocSendTime) {
        setPocSendTime(DateTime.fromISO(agent.pocSendTime));
      }
      setPocAutoSendNextWeek(agent.pocAutoSendNextWeek || false);
      setAddToWaitlistForUnscheduledVisitsOnPocCreated(agent.addToWaitlistForUnscheduledVisitsOnPocCreated || false);
    } else {
      setAutomatedPocEnabled(false);
      setPocSendDayOfWeek(0);
      setPocSendTime(DateTime.now().set({ hour: 9, minute: 0, second: 0 }));
      setPocAutoSendNextWeek(false);
      setAddToWaitlistForUnscheduledVisitsOnPocCreated(false);
    }
  }, [agent]);

  const handleSave = async () => {
    if (!agent || !user?.token) {
      showMessage("Agent data or user session not found.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.patch(`/users/super-organization/${superOrganization?.superOrganizationId}`, user.token, {
        defaultGapBetweenAppointments: defaultGapBetweenAppointments,
        autoExtendToWeeks: autoExtendToWeeks,
        numberOfWeeksToAutoExtend: numberOfWeeksToAutoExtend,
        autoExtendToAuth: autoExtendToAuth,
        autoExtendToPoc: autoExtendToPoc,
        autoTruncateAppointments: autoTruncateAppointments,
      });
      await dispatch(setSuperOrganization(response.data.superOrganization as SuperOrganization));

      const agentProps: Partial<Agent> = {
        agentId: agent.agentId,
        automatedPocEnabled: automatedPocEnabled,
        pocSendDayOfWeek: pocSendDayOfWeek,
        pocSendTime: pocSendTime?.toISO() || undefined,
        pocAutoSendNextWeek: pocAutoSendNextWeek,
        addToWaitlistForUnscheduledVisitsOnPocCreated: addToWaitlistForUnscheduledVisitsOnPocCreated,
      };
      await dispatch(updateAgentThunk({ token: user.token, agent: agentProps })).unwrap();
      showMessage("Plan of care settings updated successfully.", "success");
    } catch (error) {
      console.error("Failed to update plan of care settings:", error);
      showMessage("Failed to update plan of care settings.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!agent) {
    return (
      <FunctionalModulesLayout>
        <CircularProgress size={30} />
      </FunctionalModulesLayout>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <FunctionalModulesLayout>
        <Box pb={6}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Plan of Care
          </Typography>

          <Box display="flex" alignItems="center" ml="-10px">
            <Checkbox checked={addToWaitlistForUnscheduledVisitsOnPocCreated} onChange={(e) => setAddToWaitlistForUnscheduledVisitsOnPocCreated(e.target.checked)} sx={{ color: Colors.grey2 }} />
            <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>Add patient onto waitlist for unscheduled visits when POC created</Typography>
          </Box>

          <Box display="flex" alignItems="center" ml="-10px" mt={2}>
            <Checkbox checked={automatedPocEnabled} onChange={(e) => setAutomatedPocEnabled(e.target.checked)} sx={{ color: Colors.grey2 }} />
            <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>Automatically send booking links to patients</Typography>
          </Box>
          <Typography sx={{ fontSize: "0.9rem", color: Colors.grey1, pl: "30px" }}>
            Each week, patients who need to schedule their Plan of Care appointments will automatically receive a booking link via text message.
          </Typography>

          {automatedPocEnabled && (
            <Box sx={{ pl: "30px", mt: 2.5 }}>
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  color: Colors.grey1,
                  mb: 1.5,
                }}
              >
                Choose when to send the weekly booking link:
              </Typography>

              <Box display="flex" gap={3} flexWrap="wrap" alignItems="flex-start">
                <Box sx={{ width: "200px" }}>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: Colors.grey2,
                      mb: 0.5,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Day
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={pocSendDayOfWeek}
                      onChange={(e) => setPocSendDayOfWeek(e.target.value as number)}
                      renderValue={(selected) => {
                        const day = daysOfWeek.find((d) => d.value === selected);
                        return day ? day.label : "Select day";
                      }}
                      sx={{
                        borderRadius: "10px",
                        backgroundColor: Colors.textfield,
                        "& .MuiInputBase-input": {
                          padding: "12px 14px",
                          fontSize: "0.9rem",
                          color: "#FFF",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#FFF",
                        },
                      }}
                      MenuProps={menuPropsStyles}
                    >
                      {daysOfWeek.map((day) => (
                        <MenuItem key={day.value} value={day.value}>
                          <ListItemText primary={day.label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ width: "200px" }}>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: Colors.grey2,
                      mb: 1,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Time
                  </Typography>
                  <TimePicker
                    value={pocSendTime}
                    onChange={(newValue) => setPocSendTime(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            height: "47px",
                            backgroundColor: Colors.textfield,
                            "& .MuiInputBase-input": {
                              padding: "12px 14px",
                              fontSize: "0.9rem",
                              color: "#FFF",
                            },
                            "& .MuiSvgIcon-root": {
                              color: "#FFF",
                            },
                            "& fieldset": {},
                            "&:hover fieldset": {
                              borderColor: Colors.white,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: Colors.primary,
                              borderWidth: "2px",
                            },
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box display="flex" alignItems="center" ml="-10px" mt={1.5}>
                <Checkbox checked={pocAutoSendNextWeek} onChange={(e) => setPocAutoSendNextWeek(e.target.checked)} sx={{ color: Colors.grey2 }} />
                <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>Check patients' unscheduled visits for next week</Typography>
              </Box>
              <Typography sx={{ fontSize: "0.9rem", color: Colors.grey1, pl: "30px" }}>
                When off, patients receive a booking link if they have unscheduled visits this week. When on, patients receive a booking link if they have unscheduled visits next week.
              </Typography>
            </Box>
          )}

          <Box mt={2}>
            <Box display="flex" alignItems="center" marginTop="40px" gap="8px">
              <h5
                style={{
                  marginBottom: "0px",
                  fontSize: "0.9rem",
                }}
              >
                Default gap between appointments (days)
              </h5>
              <Chip
                label="Company-Wide"
                size="small"
                sx={{
                  backgroundColor: `${Colors.info}20`,
                  color: Colors.info,
                  border: `1px solid ${Colors.info}40`,
                  fontSize: "0.7rem",
                  height: "20px",
                }}
              />
            </Box>
            <p
              style={{
                fontSize: "0.9rem",
                marginTop: "8px",
                marginBottom: "8px",
                maxWidth: "600px",
                lineHeight: "1.5",
                color: Colors.grey1,
              }}
            >
              Set a default number of days between appointments for a patient within a week (0 means they can be consecutive, 1 means at least 1 day should be skipped, etc.)
            </p>
            <input
              type="number"
              value={defaultGapBetweenAppointments}
              onChange={(e) => setDefaultGapBetweenAppointments(Math.max(0, Number(e.target.value)))}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                background: Colors.textfield,
              }}
            />
            <Box display="flex" alignItems="center" mt={3}>
              <Checkbox checked={autoExtendToWeeks} onChange={(e) => setAutoExtendToWeeks(e.target.checked)} sx={{ color: Colors.grey2 }} />
              <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>Auto-extend plan of care to a certain amount weeks in the future (0 for up to current week) (Max 16)</Typography>
              <Chip
                label="Company-Wide"
                size="small"
                sx={{
                  backgroundColor: `${Colors.info}20`,
                  color: Colors.info,
                  border: `1px solid ${Colors.info}40`,
                  fontSize: "0.7rem",
                  height: "20px",
                  marginLeft: "8px",
                }}
              />
            </Box>
            {autoExtendToWeeks && (
              <Box sx={{ pl: "30px", mt: 1 }}>
                <input
                  type="number"
                  value={numberOfWeeksToAutoExtend}
                  onChange={(e) => setNumberOfWeeksToAutoExtend(Math.min(Math.max(0, Number(e.target.value)), 16))}
                  style={{
                    width: "100%",
                    padding: "10px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                    background: Colors.textfield,
                  }}
                />
              </Box>
            )}
            <Box display="flex" alignItems="center" mt={3}>
              <Checkbox checked={autoExtendToAuth} onChange={(e) => setAutoExtendToAuth(e.target.checked)} sx={{ color: Colors.grey2 }} />
              <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>Auto-extend plan of care based on authorization</Typography>
              <Chip
                label="Company-Wide"
                size="small"
                sx={{
                  backgroundColor: `${Colors.info}20`,
                  color: Colors.info,
                  border: `1px solid ${Colors.info}40`,
                  fontSize: "0.7rem",
                  height: "20px",
                  marginLeft: "8px",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: "0.9rem", color: Colors.grey1, pl: "30px" }}>If enabled, the plan of care will automatically extend to use up the full number of authorized visits.</Typography>
            <Box display="flex" alignItems="center" mt={3}>
              <Checkbox checked={autoExtendToPoc} onChange={(e) => setAutoExtendToPoc(e.target.checked)} sx={{ color: Colors.grey2 }} />
              <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>Auto-extend plan of care to match POC end date</Typography>
              <Chip
                label="Company-Wide"
                size="small"
                sx={{
                  backgroundColor: `${Colors.info}20`,
                  color: Colors.info,
                  border: `1px solid ${Colors.info}40`,
                  fontSize: "0.7rem",
                  height: "20px",
                  marginLeft: "8px",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: "0.9rem", color: Colors.grey1, pl: "30px" }}>
              If enabled, the plan of care will automatically extend weeks to reach the end date specified in the most recent POC. For example, if the patient receives an updated POC during a
              re-evaluation, the plan of care will automatically extend to match the new end date.
            </Typography>
            <Box display="flex" alignItems="center" mt={3}>
              <Checkbox checked={autoTruncateAppointments} onChange={(e) => setAutoTruncateAppointments(e.target.checked)} sx={{ color: Colors.grey2 }} />
              <Typography style={{ fontSize: "0.9rem", color: Colors.white }}>Auto-truncate appointments to authorization frequency</Typography>
              <Chip
                label="Company-Wide"
                size="small"
                sx={{
                  backgroundColor: `${Colors.info}20`,
                  color: Colors.info,
                  border: `1px solid ${Colors.info}40`,
                  fontSize: "0.7rem",
                  height: "20px",
                  marginLeft: "8px",
                }}
              />
            </Box>
            <Typography sx={{ fontSize: "0.9rem", color: Colors.grey1, pl: "30px" }}>
              If enabled, plans of care will automatically be truncated to match the number of visits per week specified in the patient's plan of care.
            </Typography>
            <Box mt={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PrimaryButton onClick={handleSave} disabled={isLoading}>
                {isLoading ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
              </PrimaryButton>
            </Box>
          </Box>
        </Box>
      </FunctionalModulesLayout>
    </LocalizationProvider>
  );
}
